import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';

import { HdBodygraphCenter } from '../hd-bodygraph.models';
import {
  HD_CENTER_NOT_SELF,
  HD_NOT_SELF_CARD_FIGMA_WIDTH,
  HD_NOT_SELF_FOCUS,
  getCenterImagePath,
  getNotSelfGateCount,
  getNotSelfImpactRank,
} from '../hd-center-not-self-metadata';
import { HdGateInfoService } from '../hd-gate-info.service';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../hd-content-language.service';

/**
 * Cheat-sheet card voor één Not-Self center — spiegelt de Figma "Not-Self Deck"
 * card layout. Gebruikt per-center data uit `HD_CENTER_NOT_SELF` en vertaalde
 * labels via `HdGateInfoService.*In(lang)`.
 *
 * Achtergrondkleur komt rechtstreeks uit `HD_CENTER_NOT_SELF[center].cardColor`
 * (Figma hex) — géén design-token want de kleuren zijn per kaart uniek.
 */
@Component({
  selector: 'hd-not-self-card',
  templateUrl: './hd-not-self-card.component.html',
  styleUrl: './hd-not-self-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [],
})
export class HdNotSelfCardComponent {
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  readonly center = input.required<HdBodygraphCenter>();

  protected readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);

  protected readonly info = computed(() => HD_CENTER_NOT_SELF[this.center()]);

  protected readonly focusConfig = computed(() => HD_NOT_SELF_FOCUS[this.info().focus]);

  protected readonly rank = computed(() => getNotSelfImpactRank(this.center()));

  protected readonly gateCount = computed(() => getNotSelfGateCount(this.center()));

  protected readonly imageSrc = computed(() => getCenterImagePath(this.center()));

  protected readonly centerLabel = computed(() =>
    this.gateInfo.centerLabelIn(this.center(), this.lang()),
  );

  protected readonly impactRankLabel = computed(() =>
    this.gateInfo.notSelfCardLabelIn('impactRank', this.lang()),
  );

  protected readonly undefinedLabel = computed(() =>
    this.gateInfo.notSelfCardLabelIn('undefined', this.lang()),
  );

  protected readonly gatesLabel = computed(() =>
    this.gateInfo.notSelfCardLabelIn('gates', this.lang()),
  );

  protected readonly focusKeynote = computed(() =>
    this.gateInfo.notSelfFocusKeynoteIn(this.info().focus, this.lang()),
  );

  /**
   * Image width as a percentage of the card content width, taken from the Figma
   * image-box width relative to the 185px card — keeps every center sized correctly
   * relative to the others (Root wide, Head small, …) and scales with the card.
   */
  protected readonly imageWidthPct = computed(
    () => (this.info().imageBox.w / HD_NOT_SELF_CARD_FIGMA_WIDTH) * 100,
  );

  /** Image-box aspect ratio (matches the open-form PNG), so height follows width. */
  protected readonly imageAspect = computed(
    () => `${this.info().imageBox.w} / ${this.info().imageBox.h}`,
  );
}
